import type * as React from 'react';

import { cn } from '../../lib/utils';

export type ChangeIndicatorDirection = 'positive' | 'negative' | 'neutral';

export interface ChangeIndicatorProps {
  /**
   * Numeric change. `value === 0` is the single "hide" predicate — the
   * component renders `null` for any direction when value is zero.
   * In practice the API ships `value: 0` alongside `direction: 'neutral'`, so
   * neutral cases are hidden via the zero check. The arrow/color pick logic
   * only consults direction when value !== 0, and `'positive'` is the sole
   * up/green branch — `'neutral'` and `'negative'` both draw down/red.
   */
  value: number;
  /** Visual direction. Only consulted when value !== 0. */
  direction: ChangeIndicatorDirection;
  /** Extra classes merged onto the root span. */
  className?: string;
}

const POSITIVE_COLOR = '#13D67E';
const NEGATIVE_COLOR = '#F44236';

/**
 * Inline change indicator: numeric value + directional triangle.
 *
 * Matches the overview mock: number first (13px Outfit Medium), then a small
 * 5×3 triangle pointing up (green) or down (red). `value === 0` is the
 * single "hide" predicate — we return `null` for any direction when
 * `value === 0`. Only `'positive'` draws up/green; other directions draw down/red.
 */
export const ChangeIndicator: React.FC<ChangeIndicatorProps> = ({
  value,
  direction,
  className,
}) => {
  if (value === 0) {
    return null;
  }

  const isPositive = direction === 'positive';
  const color = isPositive ? POSITIVE_COLOR : NEGATIVE_COLOR;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 font-outfit text-[13px] font-medium leading-[1.3]',
        className
      )}
      style={{ color }}
    >
      <span>{value}</span>
      <svg
        data-testid={isPositive ? 'change-arrow-up' : 'change-arrow-down'}
        width="5.13"
        height="3.21"
        viewBox="0 0 5.13 3.21"
        fill="none"
        aria-hidden="true"
        className={isPositive ? '' : 'rotate-180'}
      >
        <path
          d="M2.36318 0.0928237C2.46932 -0.0309412 2.66081 -0.0309413 2.76694 0.0928237L5.06555 2.77326C5.21347 2.94576 5.09091 3.21233 4.86367 3.21233H0.26646C0.0392188 3.21233 -0.0833478 2.94576 0.0645792 2.77326L2.36318 0.0928237Z"
          fill={color}
        />
      </svg>
    </span>
  );
};

ChangeIndicator.displayName = 'ChangeIndicator';
